import { animate, motionValue, type MotionValue } from 'motion'
import { ROTATION, shelfPosition } from './constants'
export const easeOutQuart = (t: number) => 1 - (1 - t) ** 4
export const easeInQuart = (t: number) => t ** 4
export const easeInOutQuart = (t: number) =>
  t < 0.5 ? 8 * t ** 4 : 1 - (-2 * t + 2) ** 4 / 2
export const activeEase = (t: number) =>
  t < 0.3 ? 0.3 * (t / 0.3) ** 3 : 0.3 + 0.7 * (1 - (1 - (t - 0.3) / 0.7) ** 3)
const value = (n: number) => motionValue(n)
export function tween(
  v: MotionValue<number>,
  target: number,
  ms: number,
  ease = easeOutQuart,
  delay = 0,
) {
  v.stop()
  return animate(v, target, {
    type: 'tween',
    duration: ms / 1000,
    ease,
    delay: delay / 1000,
  })
}
function wobble(v: MotionValue<number>, target: number, active = false) {
  v.stop()
  return animate(v, target, {
    type: 'spring',
    mass: active ? 0.2 : 1,
    stiffness: 80,
    damping: 6,
    restDelta: 0.0001,
    restSpeed: 0.0001,
  })
}
export function createSleeveMotion(
  index: number,
  mobile = false,
  lane = index,
) {
  let position = shelfPosition(index, mobile)
  const hoverSpring = { y: value(0), z: value(0) }
  const tiltSpring = value(ROTATION.shelfRotation)
  const wobbleSpring = { x: value(0), y: value(0) }
  const activeSpring = {
    x: value(position[0]),
    y: value(position[1]),
    z: value(0),
    rotation: value(0),
  }
  const scaleSpring = { scale: value(0), opacity: value(0) }
  const opacitySpring = value(0.45)
  const travel = value(0)
  let stopTravel: (() => void) | undefined
  let availableAt = 0
  let closeTravelEndsAt = 0
  const values = [
    ...Object.values(hoverSpring),
    tiltSpring,
    ...Object.values(wobbleSpring),
    ...Object.values(activeSpring),
    ...Object.values(scaleSpring),
    opacitySpring,
    travel,
  ]
  let tiltTimer: ReturnType<typeof setTimeout> | undefined
  let dropTimer: ReturnType<typeof setTimeout> | undefined
  let hoverAt = 0
  let lifted = false
  let active = false
  let closing = false
  let interactiveAt = 0
  let back = false
  let activePose = { scale: 1.12, y: -0.1 }
  const clearTimers = () => {
    clearTimeout(tiltTimer)
    clearTimeout(dropTimer)
  }
  const lift = () => {
    clearTimers()
    lifted = true
    hoverAt = performance.now()
    tween(hoverSpring.y, ROTATION.hoverOffset, ROTATION.hoverLiftDuration)
    tween(hoverSpring.z, 0.06, ROTATION.hoverLiftDuration)
    tween(tiltSpring, 0, ROTATION.hoverLiftDuration)
    wobble(wobbleSpring.x, 0)
    wobble(wobbleSpring.y, 0)
    tween(opacitySpring, 0, 450)
  }
  const drop = () => {
    clearTimers()
    lifted = false
    tween(hoverSpring.y, 0, ROTATION.hoverDropDuration, easeInQuart)
    tween(hoverSpring.z, 0, ROTATION.hoverDropDuration, easeInQuart)
    tween(opacitySpring, 0.45, ROTATION.hoverDropDuration, easeInQuart)
    tiltTimer = setTimeout(
      () => tween(tiltSpring, ROTATION.shelfRotation, 400, easeInQuart),
      240,
    )
    tween(wobbleSpring.x, 0, 800)
    tween(wobbleSpring.y, 0, 800)
  }
  const cancelTravel = () => {
    travel.stop()
    stopTravel?.()
    stopTravel = undefined
  }
  const move = (nextIndex: number, isMobile: boolean, delay = 0) => {
    position = shelfPosition(nextIndex, isMobile)
    cancelTravel()
    if (active) return
    if (closing) {
      const remaining = Math.max(0, closeTravelEndsAt - performance.now())
      tween(activeSpring.x, position[0], remaining, activeEase)
      tween(activeSpring.y, position[1], remaining, activeEase)
      return
    }
    const from = {
      x: activeSpring.x.get(),
      y: activeSpring.y.get(),
      z: activeSpring.z.get(),
    }
    if (
      Math.abs(from.x - position[0]) +
        Math.abs(from.y - position[1]) +
        Math.abs(from.z) <
      0.0001
    )
      return
    if (scaleSpring.scale.get() < 0.001) {
      activeSpring.x.jump(position[0])
      activeSpring.y.jump(position[1])
      activeSpring.z.jump(0)
      return
    }
    availableAt = Math.max(availableAt, performance.now() + delay + 600)
    travel.jump(0)
    stopTravel = travel.on('change', (time) => {
      const progress = easeInOutQuart(time)
      const x = from.x + (position[0] - from.x) * progress
      const y = from.y + (position[1] - from.y) * progress
      const lift = Math.min(1, time / 0.15, (1 - time) / 0.15)
      const restingDepth = from.z * (1 - progress)
      // Parallel travel planes keep tilted covers apart even as rows cross.
      const travelDepth =
        Math.tan(ROTATION.shelfRotation) * y + 0.5 + lane * 0.03
      activeSpring.x.set(x)
      activeSpring.y.set(y)
      activeSpring.z.set(
        restingDepth + (travelDepth - restingDepth) * easeOutQuart(lift),
      )
    })
    tween(travel, 1, 600, (time) => time, delay)
  }
  const enter = (delay: number) => {
    if (scaleSpring.scale.get() < 0.999 || scaleSpring.opacity.get() < 0.999)
      availableAt = Math.max(availableAt, performance.now() + delay + 400)
    tween(scaleSpring.scale, 1, 150, easeOutQuart, delay)
    tween(scaleSpring.opacity, 1, 400, easeOutQuart, delay)
  }
  const exit = (delay: number) => {
    clearTimers()
    cancelTravel()
    availableAt = Infinity
    tween(scaleSpring.scale, 0, 150, easeInQuart, delay)
    tween(scaleSpring.opacity, 0, 400, easeInQuart, delay)
  }
  const settled = () =>
    performance.now() >= availableAt &&
    // Animation clocks can lag wall time when the browser drops frames.
    !travel.isAnimating() &&
    !scaleSpring.scale.isAnimating() &&
    !scaleSpring.opacity.isAnimating()
  return {
    hoverSpring,
    tiltSpring,
    wobbleSpring,
    activeSpring,
    scaleSpring,
    opacitySpring,
    lift: () => {
      if (!active && !closing && settled()) lift()
    },
    drop: () => {
      if (!active && !closing) drop()
    },
    pointerReady() {
      return active && !closing && performance.now() >= interactiveAt
    },
    resetPoint() {
      tween(wobbleSpring.x, 0, 800)
      tween(wobbleSpring.y, 0, 800)
    },
    setActivePose(pose: { scale: number; y: number }) {
      if (pose.scale === activePose.scale && pose.y === activePose.y) return
      activePose = pose
      if (active && !closing) {
        tween(scaleSpring.scale, pose.scale, 300)
        tween(activeSpring.y, pose.y, 300)
      }
    },
    point(x: number, y: number) {
      if (closing || (active && performance.now() < interactiveAt)) return
      x = Math.max(0, Math.min(1, x))
      y = Math.max(0, Math.min(1, y))
      if (active && back) {
        x = 1 - x
        y = 1 - y
      }
      if (!active && (!lifted || performance.now() - hoverAt < 500)) return
      wobble(
        wobbleSpring.x,
        ((y - 0.5) * 2 * Math.PI) / (active ? 27 : 45),
        active,
      )
      wobble(
        wobbleSpring.y,
        ((x - 0.5) * 2 * Math.PI) / (active ? 21 : 35),
        active,
      )
    },
    isHovering() {
      return lifted && !active && !closing
    },
    canOpen() {
      return !closing && settled() && scaleSpring.scale.get() > 0.99
    },
    reconcile(
      nextIndex: number,
      isMobile: boolean,
      present: boolean,
      delay: number,
      moveDelay: number,
    ) {
      if (active || closing) {
        move(nextIndex, isMobile)
        return
      }
      availableAt = 0
      clearTimers()
      lifted = false
      tween(hoverSpring.y, 0, 90)
      tween(hoverSpring.z, 0, 90)
      tween(tiltSpring, ROTATION.shelfRotation, 90)
      tween(wobbleSpring.x, 0, 90)
      tween(wobbleSpring.y, 0, 90)
      tween(opacitySpring, 0.45, 90)
      if (present) {
        move(nextIndex, isMobile, moveDelay)
        enter(delay)
      } else exit(delay)
    },
    open() {
      cancelTravel()
      active = true
      closing = false
      back = false
      interactiveAt = performance.now() + 700
      lift()
      tween(scaleSpring.scale, activePose.scale, 700, activeEase)
      tween(activeSpring.x, 0, 700, activeEase)
      tween(activeSpring.y, activePose.y, 700, activeEase)
      tween(activeSpring.z, 6.6, 700, activeEase)
      tween(activeSpring.rotation, Math.PI / 12, 700, activeEase)
    },
    flip(nextBack: boolean) {
      back = nextBack
      tween(wobbleSpring.x, 0, 800)
      tween(wobbleSpring.y, 0, 800)
      tween(
        activeSpring.rotation,
        (back ? Math.PI : 0) + Math.PI / 12,
        700,
        activeEase,
      )
    },
    close() {
      active = false
      closing = true
      clearTimers()
      cancelTravel()
      closeTravelEndsAt = performance.now() + 700
      tween(scaleSpring.scale, 1, 700, activeEase)
      tween(activeSpring.x, position[0], 700, activeEase)
      tween(activeSpring.y, position[1], 700, activeEase)
      tween(activeSpring.z, 0, 700, activeEase)
      tween(activeSpring.rotation, 0, 700, activeEase)
      hoverSpring.y.stop()
      animate(hoverSpring.y, ROTATION.hoverOffset * 0.75, {
        type: 'spring',
        mass: 1,
        stiffness: 80,
        damping: 14,
      })
      tween(hoverSpring.z, 0, 700)
      tween(wobbleSpring.x, 0, 800)
      tween(wobbleSpring.y, 0, 800)
      dropTimer = setTimeout(() => {
        closing = false
        drop()
      }, 700)
    },
    move,
    enter,
    exit,
    dispose() {
      clearTimers()
      cancelTravel()
      values.forEach((v) => v.stop())
    },
  }
}
export type SleeveMotion = ReturnType<typeof createSleeveMotion>
